<script lang="ts">
  import {
    PASSWORD_MIN_LENGTH,
    type AdminStatsDto,
    type AdminUserDto,
    type PageViewEntryDto,
    type Role,
  } from "@piano/shared";
  import { ApiError } from "../lib/api/client.js";
  import { api } from "../lib/api/index.js";
  import { formatBytes, formatRelative } from "../lib/format.js";
  import { router } from "../lib/router.svelte.js";
  import { session } from "../lib/stores/session.svelte.js";
  import { ui } from "../lib/stores/ui.svelte.js";
  import Icon from "../components/Icon.svelte";
  import Sheet from "../components/Sheet.svelte";

  let users = $state<AdminUserDto[]>([]);
  let stats = $state<AdminStatsDto | null>(null);
  let views = $state<PageViewEntryDto[]>([]);
  let rescanning = $state(false);

  async function load(): Promise<void> {
    try {
      [users, stats, views] = await Promise.all([api.adminUsers(), api.adminStats(), api.adminPageViews(30)]);
    } catch (error) {
      ui.error(error);
    }
  }
  $effect(() => {
    void load();
  });

  // Nutzer anlegen / bearbeiten
  let editor = $state<{
    id: string | null;
    email: string;
    displayName: string;
    role: Role;
    isActive: boolean;
    password: string;
  } | null>(null);

  function openNew(): void {
    editor = { id: null, email: "", displayName: "", role: "user", isActive: true, password: "" };
  }
  function openEdit(user: AdminUserDto): void {
    editor = {
      id: user.id,
      email: user.email,
      displayName: user.displayName,
      role: user.role,
      isActive: user.isActive,
      password: "",
    };
  }
  async function save(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    if (!editor) return;
    try {
      if (editor.id) {
        await api.adminUpdateUser(editor.id, {
          displayName: editor.displayName.trim(),
          role: editor.role,
          isActive: editor.isActive,
          ...(editor.password ? { password: editor.password } : {}),
        });
        ui.toast("Gespeichert", "success");
      } else {
        await api.adminCreateUser({
          email: editor.email.trim(),
          password: editor.password,
          role: editor.role,
          ...(editor.displayName.trim() ? { displayName: editor.displayName.trim() } : {}),
        });
        ui.toast("Nutzer angelegt", "success");
      }
      editor = null;
      await load();
    } catch (error) {
      ui.error(error);
    }
  }
  async function remove(): Promise<void> {
    if (!editor?.id) return;
    const user = users.find((u) => u.id === editor?.id);
    if (!user) return;
    const ok = await ui.confirm({
      title: "Nutzer löschen?",
      message: user.documentCount
        ? `${user.displayName} besitzt ${user.documentCount} Stücke. Sie gehen an dich über.`
        : `${user.displayName} wird gelöscht.`,
      confirmLabel: "Löschen",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.adminDeleteUser(user.id, user.documentCount ? session.user?.id : undefined);
      editor = null;
      ui.toast("Nutzer gelöscht", "success");
      await load();
    } catch (error) {
      ui.error(error instanceof ApiError ? error : new Error("Löschen fehlgeschlagen"));
    }
  }

  async function rescan(): Promise<void> {
    rescanning = true;
    try {
      const result = await api.adminRescan();
      const total = result.added + result.updated + result.missing + result.restored + result.renamed;
      ui.toast(
        total
          ? `Neu: ${result.added} · geändert: ${result.updated} · umbenannt: ${result.renamed} · fehlend: ${result.missing}`
          : "Bibliothek ist aktuell",
        "success",
      );
      await load();
    } catch (error) {
      ui.error(error);
    } finally {
      rescanning = false;
    }
  }

  const typeLabel: Record<string, string> = {
    library: "Bibliothek",
    viewer: "Noten",
    playlists: "Setlists",
    admin: "Verwaltung",
    login: "Anmeldung",
  };
</script>

<div class="admin" class:narrow={!ui.wide}>
  <header>
    <button type="button" class="icon-btn" aria-label="Zurück" onclick={() => router.go("/profil")}
      ><Icon name="back" /></button
    >
    <h1>Verwaltung</h1>
  </header>

  {#if stats}
    <div class="stats">
      <div class="stat">
        <span class="value">{stats.documents.total}</span><span class="t3"
          >Stücke · {formatBytes(stats.documents.storageBytes)}</span
        >
      </div>
      <div class="stat">
        <span class="value">{stats.users.active}</span><span class="t3"
          >aktive Nutzer von {stats.users.total}</span
        >
      </div>
      <div class="stat">
        <span class="value">{stats.pageViews.last7d}</span><span class="t3">Aufrufe in 7 Tagen</span>
      </div>
      {#if stats.documents.missing || stats.documents.invalid}
        <div class="stat warn">
          <span class="value">{stats.documents.missing + stats.documents.invalid}</span>
          <span class="t3">fehlend/unlesbar</span>
        </div>
      {/if}
    </div>
  {/if}

  <section class="block">
    <div class="block-head">
      <span class="label">Nutzer</span>
      <button type="button" class="btn btn-ghost" onclick={openNew}
        ><Icon name="plus" size={18} /> Neuer Nutzer</button
      >
    </div>
    <div class="group">
      {#each users as user (user.id)}
        <button type="button" class="item" onclick={() => openEdit(user)}>
          <span class="avatar" class:inactive={!user.isActive}
            >{user.displayName.charAt(0).toUpperCase()}</span
          >
          <span class="grow">
            <span class="name"
              >{user.displayName}
              {#if user.role === "admin"}<span class="badge">Admin</span>{/if}{#if !user.isActive}<span
                  class="badge off">inaktiv</span
                >{/if}</span
            >
            <small class="t3"
              >{user.email} · {user.documentCount} Stücke · zuletzt {formatRelative(user.lastLoginAt)}</small
            >
          </span>
          <Icon name="forward" size={18} />
        </button>
      {/each}
    </div>
  </section>

  <section class="block">
    <span class="label">Bibliothek</span>
    <div class="group">
      <button type="button" class="item" disabled={rescanning} onclick={rescan}>
        <Icon name="refresh" />
        <span class="grow"
          >{rescanning ? "Lese ein …" : "Ordner „sheets“ neu einlesen"}<small class="t3"
            >Erkennt neue, geänderte und umbenannte PDFs</small
          ></span
        >
      </button>
    </div>
  </section>

  {#if views.length}
    <section class="block">
      <span class="label">Letzte Aufrufe</span>
      <div class="group views">
        {#each views as view (view.id)}
          <div class="view-row">
            <span>{typeLabel[view.pageType] ?? view.pageType}</span>
            <span class="t3">{view.userDisplayName ?? "–"}</span>
            <span class="t3">{formatRelative(view.createdAt)}</span>
          </div>
        {/each}
      </div>
    </section>
  {/if}
</div>

{#if editor}
  <Sheet title={editor.id ? "Nutzer bearbeiten" : "Neuer Nutzer"} onclose={() => (editor = null)} width={440}>
    <form class="form" onsubmit={save}>
      <h2>{editor.id ? "Nutzer bearbeiten" : "Neuer Nutzer"}</h2>
      {#if !editor.id}
        <label class="field"
          >E-Mail<input
            class="input"
            type="email"
            bind:value={editor.email}
            required
            autocomplete="off"
          /></label
        >
      {/if}
      <label class="field"
        >Anzeigename<input class="input" bind:value={editor.displayName} maxlength="120" /></label
      >
      <label class="field"
        >{editor.id
          ? "Neues Passwort (leer = unverändert)"
          : `Passwort (mind. ${PASSWORD_MIN_LENGTH} Zeichen)`}<input
          class="input"
          type="password"
          autocomplete="new-password"
          minlength={PASSWORD_MIN_LENGTH}
          bind:value={editor.password}
          required={!editor.id}
        /></label
      >
      <div class="row2">
        <button
          type="button"
          class="chip"
          aria-pressed={editor.role === "user"}
          onclick={() => editor && (editor.role = "user")}>Mitglied</button
        >
        <button
          type="button"
          class="chip"
          aria-pressed={editor.role === "admin"}
          onclick={() => editor && (editor.role = "admin")}>Administrator</button
        >
        {#if editor.id}
          <span class="grow"></span>
          <button
            type="button"
            class="opt"
            role="switch"
            aria-checked={editor.isActive}
            onclick={() => editor && (editor.isActive = !editor.isActive)}
          >
            Aktiv <span class="switch" aria-checked={editor.isActive}></span>
          </button>
        {/if}
      </div>
      <div class="actions">
        {#if editor.id && editor.id !== session.user?.id}
          <button type="button" class="btn btn-danger" onclick={remove}
            ><Icon name="trash" size={18} /> Löschen</button
          >
        {/if}
        <span class="grow"></span>
        <button type="button" class="btn btn-ghost" onclick={() => (editor = null)}>Abbrechen</button>
        <button type="submit" class="btn btn-primary">Speichern</button>
      </div>
    </form>
  </Sheet>
{/if}

<style>
  .admin {
    padding: calc(28px + var(--safe-top)) 32px 40px;
    display: flex;
    flex-direction: column;
    gap: 22px;
    max-width: 820px;
  }
  .narrow {
    padding: calc(16px + var(--safe-top)) 18px 24px;
  }
  header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-left: -10px;
  }
  h1 {
    font-size: 32px;
  }
  .stats {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 12px;
  }
  .stat {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 16px;
    border-radius: var(--r-lg);
    background: var(--surface-2);
    font-size: 13px;
    font-weight: 700;
  }
  .stat.warn .value {
    color: var(--accent-2);
  }
  .value {
    font-family: var(--font-display);
    font-size: 30px;
    font-weight: 800;
  }
  .block {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .block-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-left: 8px;
  }
  .group {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 8px;
    border-radius: 18px;
    background: var(--surface-2);
  }
  .item {
    display: flex;
    align-items: center;
    gap: 14px;
    min-height: 58px;
    padding: 0 12px;
    border-radius: var(--r-md);
    text-align: left;
    font-weight: 600;
    transition: background var(--dur-tap);
  }
  .item:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.06);
  }
  .item :global(svg) {
    color: var(--text-2);
  }
  .avatar {
    width: 38px;
    height: 38px;
    border-radius: 50%;
    background: var(--accent);
    color: var(--on-accent);
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 800;
  }
  .avatar.inactive {
    background: var(--raised-2);
    color: var(--text-3);
  }
  .grow {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }
  .name {
    font-weight: 800;
  }
  small {
    font-size: 12px;
    font-weight: 600;
  }
  .badge {
    margin-left: 6px;
    padding: 1px 8px;
    border-radius: var(--r-pill);
    background: var(--accent-soft);
    color: var(--accent);
    font-size: 11px;
  }
  .badge.off {
    background: rgba(255, 255, 255, 0.08);
    color: var(--text-3);
  }
  .views {
    padding: 8px 16px;
  }
  .view-row {
    display: grid;
    grid-template-columns: 1fr 1fr auto;
    gap: 12px;
    padding: 8px 0;
    font-size: 13px;
    font-weight: 600;
    border-bottom: 1px solid var(--line);
  }
  .view-row:last-child {
    border-bottom: 0;
  }
  .form {
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 0 8px;
  }
  .form h2 {
    font-size: 22px;
  }
  .row2 {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }
  .opt {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    font-weight: 700;
  }
  .actions {
    display: flex;
    gap: 10px;
    align-items: center;
  }
  .actions .grow {
    flex: 1;
  }
</style>
